import React, { useState, useRef, useEffect } from 'react';
import { useAnnouncements, ANNOUNCEMENT_CATEGORIES } from '../contexts/AnnouncementContext';
import './AnnouncementForm.css';

function AnnouncementForm({ onCancel, announcementToEdit }) {
  const { addAnnouncement, editAnnouncement } = useAnnouncements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const isEditing = !!announcementToEdit;
  
  // Form state
  const [title, setTitle] = useState(announcementToEdit?.title || '');
  const [content, setContent] = useState(announcementToEdit?.content || '');
  const [category, setCategory] = useState(announcementToEdit?.category || 'General');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(announcementToEdit?.imageBase64 || '');
  const [keepExistingImage, setKeepExistingImage] = useState(!!announcementToEdit?.imageBase64);
  
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }
    
    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('Image file is too large. Maximum size is 5MB.');
      return;
    }
    
    setImage(file);
    setKeepExistingImage(false);
    
    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview('');
    setKeepExistingImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (title.trim() === '') {
        throw new Error('Title is required');
      }
      
      if (content.trim() === '') {
        throw new Error('Content is required');
      }
      
      let announcementResult;
      
      if (isEditing) {
        announcementResult = await editAnnouncement(announcementToEdit.id, {
          title,
          content,
          category,
          imageFile: image,
          keepExistingImage
        });
        setSuccess('Announcement updated successfully!');
      } else {
        announcementResult = await addAnnouncement(title, content, category, image);
        setSuccess('Announcement posted successfully!');
        
        // Clear form if not editing
        setTitle('');
        setContent('');
        setCategory('General');
        setImage(null);
        setImagePreview('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
      
      // Auto-close the form after 2 seconds on success
      setTimeout(() => {
        if (onCancel) onCancel();
      }, 2000);
      
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Scroll to form on mount
  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  return (
    <div className="announcement-form" ref={formRef}>
      <h3 className="form-title">
        {isEditing ? 'Edit Announcement' : 'Post New Announcement'}
      </h3>
      
      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title" className="form-label">Title</label>
          <input
            type="text"
            id="title"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter announcement title"
            required
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Category</label>
          <div className="category-selector">
            {ANNOUNCEMENT_CATEGORIES.map(cat => (
              <div 
                key={cat} 
                className={`category-option ${category === cat ? 'selected' : ''}`}
                onClick={() => setCategory(cat)}
              >
                {cat}
              </div>
            ))}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="content" className="form-label">Content</label>
          <textarea
            id="content"
            className="form-control"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter announcement content"
            rows="6"
            required
          />
        </div>
        
        <div className="form-group image-upload-container">
          <label className="form-label">Announcement Image (Optional)</label>
          
          <div className="image-upload-controls">
            <input
              type="file"
              id="image"
              ref={fileInputRef}
              accept="image/jpeg, image/png, image/gif, image/webp"
              onChange={handleImageChange}
              className="image-file-input"
            />
            <label htmlFor="image" className="image-upload-button">Choose Image</label>
            
            {(imagePreview || keepExistingImage) && (
              <button
                type="button"
                className="image-remove-button"
                onClick={handleRemoveImage}
              >
                Remove Image
              </button>
            )}
          </div>
          
          {imagePreview && (
            <div className="image-preview-container">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="image-preview" 
              />
              <p className="image-info">
                {isEditing && !image && keepExistingImage 
                  ? 'Current image will be kept' 
                  : image 
                    ? `Selected image: ${image.name} (${(image.size / 1024 / 1024).toFixed(2)}MB)` 
                    : ''}
              </p>
            </div>
          )}
          
          {isEditing && announcementToEdit.imageBase64 && !imagePreview && (
            <div className="form-note">
              <p>The current image will be removed.</p>
            </div>
          )}
        </div>
        
        {/* Submit and Cancel buttons */}
        <div className="form-actions" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
            style={{
              background: isEditing 
                ? 'linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)' 
                : 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
              fontSize: '1.3rem',
              fontWeight: '700',
              padding: '1.5rem 2.5rem',
              width: '100%',
              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            {isLoading ? 'Saving...' : isEditing ? 'UPDATE ANNOUNCEMENT' : 'SUBMIT ANNOUNCEMENT'}
            <span style={{ 
              position: 'absolute',
              right: '20px',
              fontSize: '1.4rem'
            }}>
              {isEditing ? '✏️' : '✅'}
            </span>
          </button>
          
          <button
            type="button"
            className="cancel-button"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AnnouncementForm; 